import './PBFLandingPage.style.less'

import { Col, Row, Spin } from 'antd'
import { useEffect, useRef, useState } from 'react'
import OrderTable from './components/OrderTable/OrderTable'
import PBFMenuTile from './components/PBFMenuTile/PBFMenuTile'
import {
  AlertFilled,
  CalendarFilled,
  CheckCircleFilled,
  FolderOpenOutlined,
  WarningFilled,
} from '@ant-design/icons'
import Header from '@/layout/components/Header'
import { useAtom } from 'jotai'
import { orderCreationTabNumberAtom } from '../../jotai/order.jotai'
import Fade from '@/components/Fade'
import { useNavigate } from 'react-router-dom'
import { getUrlParams, isEmptyValue, setUrlParams } from '@/utils/common'
import i18n from '@/config/translation'
import { t } from 'i18next'
import { useQuery } from 'react-query'
import { PBF_QUERY_KEY } from '@/constant/pbf'
import { orderAdvanceSummary } from '../Order.service'

const { GET_ORDER_SUMMARY } = PBF_QUERY_KEY

export enum Tile {
  Urgent = 'total_alert_task',
  Warning = 'total_notify_task',
  OnTrack = 'total_done_task',
  Delivered = 'delivered',
  Active = '',
}

const defaultSummaryValue = {
  totalOrders: 0,
  activeOrders: 0,
  urgent: 0,
  warning: 0,
  onTrack: 0,
  delivered: 0,
}

interface Props {
  hideActions?: any
}

function PBFLandingPage(props: Props) {
  const hideActions = props.hideActions?.[0] ?? {}
  const [, setactiveTab] = useAtom(orderCreationTabNumberAtom)
  const getFilterParams = getUrlParams('filter') || 'all'

  const childRef: any = useRef()

  const [selectedFilter, setSelectedFilter] = useState<any>(getFilterParams)

  const navigate = useNavigate()

  useEffect(() => {
    setactiveTab('1')
  }, [])

  const onTileClick = (current?: Tile) => {
    navigate({
      search: `?${setUrlParams('filter', current ?? '')}`,
    })
    navigate({
      search: `?${setUrlParams('page', '1')}`,
    })
    setSelectedFilter(current ?? '')
    childRef?.current?.resetPage()
  }

  // Refetch data when the "My Order" filter is applied and the useQuery action of PBF_ORDERS is triggered
  const { data = defaultSummaryValue } = useQuery(
    [GET_ORDER_SUMMARY],
    async () => {}
  )

  const handleFetchData = async () => {}

  useEffect(() => {
    setSelectedFilter(getUrlParams('filter') ?? '')
    handleFetchData()
  }, [])

  const activeOrders = () => {
    const { urgent = 0, warning = 0, onTrack = 0, activeOrders = 0 } = data
    return [
      urgent,
      warning,
      onTrack,
      activeOrders - (urgent + warning + onTrack),
    ]
  }

  return (
    <>
      <Header
        icon={<FolderOpenOutlined className="icon" />}
        title={`My ${i18n.t('orders')}`}
        background="linear-gradient(181.35deg, #3B719A 1.15%, rgba(74, 141, 192, 0.9) 98.85%)"
      />
      <div className="pbf-landing-page-container order-landing-page-container">
        <Fade>
          <Row
            className="order-summary-tiles"
            gutter={[
              { xs: 12, sm: 12, md: 16, lg: 0 },
              { xs: 12, sm: 16, md: 16, lg: 0 },
            ]}
            justify="space-between"
            align="middle"
          >
            <Col xs={12} sm={8} md={8} lg={4}>
              <Spin spinning={false}>
                <PBFMenuTile
                  title={
                    <>
                      <span>Active</span>
                      <br />
                      <span>{t('orders')}</span>
                    </>
                  }
                  data={activeOrders()}
                  isSelected={isEmptyValue(selectedFilter)}
                  qty={data.activeOrders}
                  color="#5B5B5B"
                  isProgress
                  onClick={() => onTileClick(Tile.Active)}
                />
              </Spin>
            </Col>
            <Col xs={12} sm={8} md={8} lg={4}>
              <Spin spinning={false}>
                <PBFMenuTile
                  title="Urgent"
                  isSelected={selectedFilter === Tile.Urgent}
                  qty={data.urgent}
                  icon={<AlertFilled />}
                  color="#C03261"
                  onClick={() => {
                    onTileClick(Tile.Urgent)
                  }}
                />
              </Spin>
            </Col>
            <Col xs={12} sm={8} md={8} lg={4}>
              <Spin spinning={false}>
                <PBFMenuTile
                  isSelected={selectedFilter === Tile.Warning}
                  title="Warning"
                  qty={data.warning}
                  icon={<WarningFilled />}
                  color="#EF9747"
                  onClick={() => {
                    onTileClick(Tile.Warning)
                  }}
                />
              </Spin>
            </Col>
            <Col xs={12} sm={8} md={8} lg={4}>
              <Spin spinning={false}>
                <PBFMenuTile
                  isSelected={selectedFilter === Tile.OnTrack}
                  title="On-track"
                  qty={data.onTrack}
                  icon={<CalendarFilled />}
                  color="#4A8DC0"
                  onClick={() => {
                    onTileClick(Tile.OnTrack)
                  }}
                />
              </Spin>
            </Col>
            <Col xs={12} sm={8} md={8} lg={4}>
              <Spin spinning={false}>
                <PBFMenuTile
                  isSelected={selectedFilter === Tile.Delivered}
                  title="Delivered"
                  qty={data.delivered}
                  icon={<CheckCircleFilled />}
                  color="#65BF73"
                  onClick={() => {
                    onTileClick(Tile.Delivered)
                  }}
                />
              </Spin>
            </Col>
          </Row>
          <div style={{ marginTop: 18 }}>
            <OrderTable
              sortBy={selectedFilter}
              ref={childRef}
              hideActions={hideActions}
            />
          </div>
        </Fade>
      </div>
    </>
  )
}

export default PBFLandingPage
